import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { projects } from "@/data/site";

export const ProjectCard = ({ project, index }) => (
  <Reveal delay={index * 0.08}>
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`project-card-${project.slug}`}
      className="group block"
    >
      <div
        className="relative aspect-[16/9] overflow-hidden border border-white/10"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
          e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
        }}
      >
        <div className="spotlight-glow" />
        <img
          src={project.image}
          alt={`${project.name} — ${project.industry} website by Kronos`}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.06] ${project.imgPos || "object-center"}`}
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0B0C0E]/60 via-transparent to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-25" />
        {project.concept && (
          <span
            data-testid={`concept-badge-${project.slug}`}
            className="absolute left-4 top-4 z-[3] border border-white/20 bg-[#0B0C0E]/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C0C4CE] backdrop-blur-md"
          >
            Concept Project
          </span>
        )}
        <div className="absolute right-5 top-5 z-[3] flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-[#0B0C0E]/60 text-[#F4F4F6] opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100">
          <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#5A5E6B]">{project.industry}</p>
          <h3 className="mt-2 font-display text-xl font-semibold text-[#F4F4F6] transition-colors group-hover:text-[#2563EB] sm:text-2xl">
            {project.name}
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#9498A4]">{project.summary}</p>
        </div>
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#5A5E6B] transition-colors group-hover:text-[#2563EB]">
          Enter live site <ArrowUpRight size={13} />
        </p>
      </div>
    </a>
  </Reveal>
);

export const SelectedWork = () => (
  <section data-testid="selected-work-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionTag testId="selected-work-tag">Chapter 03 — Portfolio</SectionTag>
          <Reveal>
            <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
              Selected Work
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Link
            to="/work"
            data-testid="selected-work-view-all-link"
            className="link-underline text-sm text-[#9498A4] transition-colors hover:text-[#F4F4F6]"
          >
            View all work
          </Link>
        </Reveal>
      </div>
      <div className="space-y-20">
        {projects.map((p, i) => (
          <ProjectCard key={p.slug} project={p} index={i} />
        ))}
      </div>
    </div>
  </section>
);
